/** Branding helpers for admin panel (mirrors fe roomBranding resize rules). */

export interface RoomBranding {
  displayName?: string;
  colors?: string[];
  website?: string;
  allowWebsiteCrawl?: boolean;
  logoDataUrl?: string;
  backgroundDataUrl?: string;
}

const MAX_LOGO_EDGE = 256;
const MAX_LOGO_CHARS = 200_000;
const MAX_BACKGROUND_EDGE = 1600;
const MAX_BACKGROUND_CHARS = 500_000;

type ImageResizeOpts = {
  maxEdge: number;
  maxChars: number;
};

function fileToBrandingImageDataUrl(file: File, opts: ImageResizeOpts): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('NOT_IMAGE'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('READ_FAILED'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, opts.maxEdge / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('CANVAS'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const preferPng = file.type === 'image/png' || file.type === 'image/webp';
        let dataUrl = canvas.toDataURL(preferPng ? 'image/png' : 'image/jpeg', 0.85);
        if (dataUrl.length > opts.maxChars) {
          dataUrl = canvas.toDataURL('image/jpeg', 0.72);
        }
        if (dataUrl.length > opts.maxChars) {
          dataUrl = canvas.toDataURL('image/jpeg', 0.55);
        }
        if (dataUrl.length > opts.maxChars) {
          reject(new Error('TOO_LARGE'));
          return;
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('DECODE_FAILED'));
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

export function fileToBrandingLogoDataUrl(file: File): Promise<string> {
  return fileToBrandingImageDataUrl(file, {
    maxEdge: MAX_LOGO_EDGE,
    maxChars: MAX_LOGO_CHARS,
  });
}

export function fileToBrandingBackgroundDataUrl(file: File): Promise<string> {
  return fileToBrandingImageDataUrl(file, {
    maxEdge: MAX_BACKGROUND_EDGE,
    maxChars: MAX_BACKGROUND_CHARS,
  });
}

export function buildBrandingSavePayload(form: {
  displayName: string;
  colors: string[];
  website: string;
  allowWebsiteCrawl: boolean;
  /** `undefined` = leave unchanged (omit from patch); `null`/'' = clear; string = set. */
  logoDataUrl?: string | null;
  backgroundDataUrl?: string | null;
}): Partial<RoomBranding> {
  let website = form.website.trim();
  if (website && !/^https?:\/\//i.test(website)) {
    website = `https://${website}`;
  }
  const payload: Partial<RoomBranding> = {
    displayName: form.displayName.trim(),
    colors: form.colors.slice(0, 3),
    website,
    allowWebsiteCrawl: form.allowWebsiteCrawl,
  };
  // Omit image keys when unknown so text-only admin edits don't wipe live DO assets.
  if (form.logoDataUrl !== undefined) {
    payload.logoDataUrl = form.logoDataUrl ?? '';
  }
  if (form.backgroundDataUrl !== undefined) {
    payload.backgroundDataUrl = form.backgroundDataUrl ?? '';
  }
  return payload;
}
